import { Component, DestroyRef, effect, inject, OnInit, untracked } from '@angular/core';
import { Subject, switchMap, map, catchError, of, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardFilters, DashboardService, MessagingFiltersPayload, MessagingGroupingFiltersPayload } from '../../../services/dashboard.service';
import { MessagingService } from '../../../services/messaging.service';
import { MessagingScreenComponent } from '../messaging-screen.component'; 
import { GroupingTypeKey, MessagingGroupingResponse, MessagingResponse, THEMES } from '../types';
import { MessagingCategoryTableComponent } from '../messaging-category-table/messaging-category-table.component';
import { BrandHeaderComponent } from '../brand-header/brand-header.component';
import { SpinnerIconComponent } from '../../../shared/components/icons/spinner-icon/spinner-icon.component';
import { InfiniteScrollDirective } from '../../../shared/directives/infinite-scroll.directive';
import { CommonModule } from '@angular/common';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-marketing-report',
  imports: [MessagingCategoryTableComponent, BrandHeaderComponent, SpinnerIconComponent, InfiniteScrollDirective, CommonModule, AssetVersionPipe],
  templateUrl: './marketing-report.component.html',
  styleUrl: './marketing-report.component.scss'
})
export class MarketingReportComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  protected messagingService = inject(MessagingService);
  private messagingScreen = inject(MessagingScreenComponent); 
  private destroyRef = inject(DestroyRef);

  public readonly MAX_BRAND_COLUMNS = 6;
  public readonly LIMIT = 10;
  public messagingData$ = new Subject<MessagingFiltersPayload>();
  public messagingData: MessagingResponse | undefined;
  public messagingGroupingData$ = new Subject<MessagingGroupingFiltersPayload>();
  public messagingGroupingData: MessagingGroupingResponse | undefined;
  public isLoading: boolean = false;
  private currentOffset: number = 0;
  public groupingType: GroupingTypeKey = THEMES

  constructor() {
    effect(() => {
      const dashboardFilters = this.dashboardService.brandsFilters();
      const groupingType: GroupingTypeKey = this.messagingScreen.activeGroupingType();

      untracked(() => {
        if (dashboardFilters.myBrand?.key) {
          this.groupingType = groupingType;
          this.messagingData = undefined;
          this.messagingGroupingData = undefined;
          this.currentOffset = 0;
          if (groupingType === THEMES) {
            this.messagingData$.next(this.getMessagingPayload(dashboardFilters, groupingType, this.currentOffset));
          } else {
            this.messagingGroupingData$.next(this.getMessagingGroupingPayload(dashboardFilters, groupingType));
          }
        }
      });
    });
  }

  ngOnInit(): void {
    this.messagingData$.pipe(
      switchMap(payload => {
        const isInitialLoad = payload.offset === 0;
        this.isLoading = true;
        return this.messagingService.getMessagingData(payload).pipe(
          map(result => ({ result, isInitialLoad })),
          catchError(error => {
            console.error('Error fetching messaging data', error);
            this.isLoading = false;
            return of(null);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      if (!response) return;
      const { result, isInitialLoad } = response;
      if (isInitialLoad) {
        this.messagingData = result;
      } else {
        if (this.messagingData && result.categories.length > 0) {
          this.messagingData.categories = [...this.messagingData.categories, ...result.categories];
        }
      }
      this.isLoading = false;
    });

    this.messagingGroupingData$.pipe(
      switchMap(payload => {
        this.isLoading = true;
        return this.messagingService.getMessagingGroupingData(payload).pipe(
          map(result => ({ result })),
          catchError(error => {
            console.error('Error fetching messaging grouping data', error);
            this.isLoading = false;
            return of(null);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      if (!response) return;
      this.messagingGroupingData = response.result;
      this.isLoading = false;
    });
  }

  get isThemesGrouping(): boolean {
    return this.messagingScreen.activeGroupingType() === THEMES;
  }

  onLoadMore(): void {
    const dashboardFilters = this.dashboardService.brandsFilters();
    if (dashboardFilters.myBrand?.key && !this.isLoading && this.groupingType === THEMES) {
      this.currentOffset += this.LIMIT;
      this.messagingData$.next(this.getMessagingPayload(dashboardFilters, this.groupingType, this.currentOffset));
    }
  }
  
  private getMessagingPayload(dashboardFilters: DashboardFilters, groupingType: GroupingTypeKey, offset: number): MessagingFiltersPayload {
    const payload = this.dashboardService.getDashboardFiltersPayload(dashboardFilters);
    return {
      ...payload,
      brandKey: dashboardFilters.myBrand.key,
      competitorsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      offset,
      limit: this.LIMIT,
      groupingType
    };
  }

  private getMessagingGroupingPayload(dashboardFilters: DashboardFilters, groupingType: GroupingTypeKey): MessagingGroupingFiltersPayload {
    const payload = this.dashboardService.getDashboardFiltersPayload(dashboardFilters);
    return {
      ...payload,
      brandKey: dashboardFilters.myBrand.key,
      competitorsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      groupingType
    };
  }
}

